import type { FC, ReactNode } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Tooltip } from '@mui/material';

import { Icon } from '../Icon';
import { Scrollbar } from '../Scrollbar';
import { Typography } from '../Typography';
import { NavigationButtons } from '../NavigationButtons';
import { theme } from '../../styles/theme';

const TRUNCATE_MAX_LENGTH = 160;

type BaseProps = {
  closeCard: (value: boolean) => void;
  expand?: boolean | null;
  handleExpand?: (value: boolean | null) => void;
  onNavigatePrevious?: () => void;
  onNavigateNext?: () => void;
  width?: string;
};

type HeaderField = {
  label: string;
  value: ReactNode;
  onClick?: () => void;
  tooltip?: string;
  icon?: ReactNode;
  color?: string;
};

type ContentProps = {
  headerFields: HeaderField[];
  description?: string;
  actionButton?: ReactNode;
  children?: ReactNode;
};

type Props = BaseProps & ContentProps;

export const MapInfoCard: FC<Props> = ({
  closeCard,
  headerFields,
  description,
  actionButton,
  children,
  expand,
  handleExpand,
  onNavigatePrevious,
  onNavigateNext,
  width,
}) => {
  const { t } = useTranslation();

  const [closeHover, setCloseHover] = useState(false);

  const isCollapsed = !expand;
  const hasNavigation = Boolean(onNavigatePrevious || onNavigateNext);

  return (
    <Box
      sx={{
        background: 'rgba(255, 255, 255, 0.85)',
        borderRadius: '12.5px',
        p: 2,
        width: width || '100%',
        height: isCollapsed ? 'auto' : '100%',
        minHeight: isCollapsed ? 'fit-content' : undefined,
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        pointerEvents: 'auto',
        zIndex: 1001,
      }}
    >
      <Box
        component="button"
        onClick={() => closeCard(false)}
        onMouseEnter={() => setCloseHover(true)}
        onMouseLeave={() => setCloseHover(false)}
        sx={{
          position: 'absolute',
          top: theme.spacing(2),
          right: theme.spacing(2),
          border: 'none',
          outline: 'none',
          backgroundColor: 'transparent',
          cursor: 'pointer',
          p: 0,
          zIndex: 1,
        }}
      >
        {closeHover ? (
          <Icon variant="CloseHover" width={20} height={20} />
        ) : (
          <Icon variant="Close" width={20} height={20} />
        )}
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flexShrink: 0,
          pr: 6, // Leave space for close button
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {headerFields.map((field, index) => (
            <Box key={index} sx={{ display: 'flex', flexDirection: 'column' }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  '& > *:first-child': {
                    textTransform: 'uppercase',
                  },
                }}
              >
                <Typography variant="p3" fontWeight={400}>
                  {field.label}
                </Typography>
                {field.onClick ? (
                  <Box
                    component="button"
                    onClick={field.onClick}
                    sx={{
                      border: 'none',
                      background: 'none',
                      padding: 0,
                      fontFamily: 'inherit',
                      fontSize: 'inherit',
                      textAlign: 'left',
                      cursor: 'pointer',
                      color: theme.colors.link,
                      '&:hover': {
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    <Typography variant="p1">{field.value}</Typography>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      ...(field.label.toLowerCase().includes('date')
                        ? { fontStyle: 'italic', opacity: 0.8 }
                        : {}),
                    }}
                  >
                    {field.tooltip ? (
                      <Tooltip title={field.tooltip} arrow>
                        <Box
                          component="span"
                          tabIndex={0}
                          sx={{
                            cursor: 'help',
                            color:
                              field.color ||
                              (index === 0 ? 'black' : undefined),
                          }}
                        >
                          <Typography
                            variant="p1"
                            fontWeight={index === 0 ? 600 : undefined}
                            preserveReactNodes
                          >
                            {field.value}
                          </Typography>
                        </Box>
                      </Tooltip>
                    ) : (
                      <Typography
                        variant="p1"
                        fontWeight={index === 0 ? 600 : undefined}
                        preserveReactNodes
                        {...(field.color && { style: { color: field.color } })}
                      >
                        {field.value}
                      </Typography>
                    )}
                    {field.icon && field.icon}
                  </Box>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      {children ? (
        <Box sx={{ marginTop: 1 }}>{children}</Box>
      ) : (
        description && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              marginTop: 1,
              flex: isCollapsed ? 'none' : 1,
              minHeight: isCollapsed ? undefined : 0,
            }}
          >
            <Box
              sx={{
                flex: isCollapsed ? 'none' : 1,
                minHeight: isCollapsed ? undefined : 0,
                overflow: isCollapsed ? undefined : 'hidden',
              }}
            >
              {expand ? (
                <Box sx={{ height: '100%', maxHeight: '100%' }}>
                  <Scrollbar>
                    <Typography variant="p3" fontWeight={400}>
                      {description}
                    </Typography>
                  </Scrollbar>
                </Box>
              ) : (
                <Typography variant="p3" fontWeight={400}>
                  {description.length > TRUNCATE_MAX_LENGTH
                    ? `${description.slice(0, TRUNCATE_MAX_LENGTH).trim()}...`
                    : description}
                </Typography>
              )}
            </Box>

            {description.length > TRUNCATE_MAX_LENGTH && handleExpand && (
              <Box
                component="button"
                onClick={() => handleExpand(expand ? null : true)}
                sx={{
                  border: 'none',
                  outline: 'none',
                  backgroundColor: 'transparent',
                  alignSelf: 'flex-end',
                  flexShrink: 0,
                  cursor: 'pointer',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                <Typography variant="p3">
                  {expand ? t('card.showLess') : t('card.showMore')}
                </Typography>
              </Box>
            )}
          </Box>
        )
      )}

      {actionButton && (
        <Box
          sx={{
            marginTop: 2,
            alignSelf: 'flex-start',
            flexShrink: 0,
          }}
        >
          {actionButton}
        </Box>
      )}

      {hasNavigation && (
        <Box
          sx={{
            flexShrink: 0,
            marginTop: isCollapsed ? 2 : 'auto',
            paddingTop: isCollapsed ? 0 : 2,
          }}
        >
          <NavigationButtons
            onNavigatePrevious={onNavigatePrevious}
            onNavigateNext={onNavigateNext}
          />
        </Box>
      )}
    </Box>
  );
};
